import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useUIStore, useToastStore } from '@/stores';
import { useRooms } from '@/entities/room';
import { useEstimate } from '@/features/estimate';
import { useDatabase } from '@/entities/service';
import { Service } from '@/shared/model';

import { ServiceSelectorModal } from '@/features/database/ui/modals/ServiceSelectorModal';
import { ConfirmationModal } from '@/shared/ui/ConfirmationModal';

export const EstimateModals: FC = () => {
  const { t } = useTranslation();
  const activeModal = useUIStore((s) => s.activeModal);
  const modalData = useUIStore((s) => s.modalData);
  const closeModal = useUIStore((s) => s.closeModal);
  const openModal = useUIStore((s) => s.openModal);
  const addToast = useToastStore((s) => s.addToast);
  const { addServicesToRoom, addServicesToProject, deleteRoom } = useRooms();
  const { activeRoom } = useEstimate();
  const { services, categories, units, createService, addLayerToRecipe } = useDatabase();

  const handleServiceSelect = (service: Service & { saveToDb?: boolean }) => {
      let finalService = service;
      if (service.isCustom && service.saveToDb) {
          finalService = createService(service);
      }

      if (modalData?.target === 'recipe' && modalData.recipeId && modalData.category) {
          type DraftLayer = { serviceId: string; coefficient: number };
          type DraftRecipe = { id: unknown; name: unknown; layers: DraftLayer[] };
          const existingDraft = modalData.draftRecipe as DraftRecipe | undefined;
          const draftRecipe: DraftRecipe = existingDraft ?? { id: modalData.recipeId, name: '', layers: [] };
          const newLayer: DraftLayer = { serviceId: finalService.id, coefficient: 1 };
          const updatedDraft: DraftRecipe = {
            ...draftRecipe,
            layers: [...(draftRecipe.layers ?? []), newLayer],
          };
          useUIStore.setState({
            modalData: {
              ...modalData,
              draftRecipe: updatedDraft
            }
          });
          addToast(t('recipes.editor.service_added', 'Услуга добавлена в пакет'), 'success');
      } else if (modalData?.target === 'global') {
          addServicesToProject([finalService]);
          addToast(t('editor.messages.added_to_global', 'Услуга добавлена в глобальный слой'), 'success');
      } else if (activeRoom) {
          addServicesToRoom(activeRoom.id, [finalService]);
      }
  };

  return (
    <>
      {activeModal === 'service_selector' && (
        <ServiceSelectorModal 
          isOpen={true}
          onClose={() => {
              if (modalData?.returnTo === 'recipe_editor' && modalData.recipeId) {
                  openModal('recipe_editor', { 
                    id: modalData.recipeId as string, 
                    category: modalData.category as string,
                    initialRecipe: modalData.draftRecipe,
                    returnTo: modalData.returnTo
                  });
              } else {
                  closeModal();
              }
          }}
          services={services}
          categories={categories}
          units={units}
          onSelect={handleServiceSelect}
          title={modalData?.title as string || 
            (modalData?.target === 'global' ? 'Добавить в глобальный слой' : 
             activeRoom ? t('editor.modals.add_to_room', { room: activeRoom.displayName, defaultValue: `Добавить в "${activeRoom.displayName}"` }) : 
             t('services.select_title', 'Выбрать услугу'))}
        />
      )}

      {activeModal === 'room_delete_confirm' && (
        <ConfirmationModal 
          isOpen={true}
          onClose={closeModal}
          onConfirm={() => { if(modalData?.id) deleteRoom(modalData.id as string); closeModal(); }}
          title={t('editor.modals.delete_room_title', 'Удалить комнату')}
          message={t('editor.modals.delete_room_message', 'Вы уверены?')}
        />
      )}
    </>
  );
};